// Base
import { BaseApi } from 'api/base.api'

// Interface
import {
  IHomeCertificationAgencyApi,
  IHomeCertificationAgencyApiRequest,
  IHomeCertificationAgencyApiGetAllAttrs,
  IHomeCertificationAgencyApiGetAllResponse,
  IHomeCertificationAgencyApiGetAttrs,
  IHomeCertificationAgencyApiGetResponse,
  IHomeCertificationAgencyApiSaveAttrs,
  IHomeCertificationAgencyApiSaveResponse,
  IHomeCertificationAgencyApiGetImagesAttrs,
  IHomeCertificationAgencyApiGetImagesResponse,
  IHomeCertificationAgencyApiUpdateImageAttrs,
  IHomeCertificationAgencyApiUpdateImageResponse,
  IHomeCertificationAgencyApiDeleteImageAttrs,
  IHomeCertificationAgencyApiDeleteImageResponse,
} from 'interfaces/back-office/content-management/home/certification-agency/home-certification-agency-api.interface'
import { IBaseApiRequestPayload } from 'interfaces/api'

// Constant
import { ApiConstant } from 'constant'

// Utils
import { axios_renderUrl } from 'utils'

export class HomeCertificationAgencyApi
  extends BaseApi<IHomeCertificationAgencyApi>
  implements IHomeCertificationAgencyApiRequest
{
  readonly url = ApiConstant.CONTENT_HOME_CERTIFICATION_AGENCY_URL
  private certificationAgencyItemsUrl = axios_renderUrl(`${this.url}/items` as any, ApiConstant.BACK_OFFICE)
  public certificationAgencyContentUrl = axios_renderUrl(`${this.url}/content` as any, ApiConstant.BACK_OFFICE)

  /**
   * @description Get home certification agencies
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IHomeCertificationAgencyApiGetAllAttrs} payload
   *
   * @return {Promise<IHomeCertificationAgencyApiGetAllResponse>} Promise<IHomeCertificationAgencyApiGetAllResponse>
   */
  async homeCertificationAgency_getHomeCertificationAgencies(
    payload: IHomeCertificationAgencyApiGetAllAttrs,
  ): Promise<IHomeCertificationAgencyApiGetAllResponse> {
    try {
      const response = await this.POST(`${this.certificationAgencyItemsUrl}/list`, payload.body, { ...payload.requestConfig })

      return Promise.resolve(response)
    } catch (err) {
      return Promise.reject(err)
    }
  }

  /**
   * @description Get home certification agency
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IHomeCertificationAgencyApiGetAttrs} payload
   *
   * @return {Promise<IHomeCertificationAgencyApiGetResponse>} Promise<IHomeCertificationAgencyApiGetResponse>
   */
  async homeCertificationAgency_getHomeCertificationAgency(
    payload: IHomeCertificationAgencyApiGetAttrs,
  ): Promise<IHomeCertificationAgencyApiGetResponse> {
    try {
      const response = await this.POST(
        `${this.certificationAgencyItemsUrl}/${payload.params.id}`,
        {},
        { ...payload.requestConfig },
      )

      return Promise.resolve(response)
    } catch (err) {
      return Promise.reject(err)
    }
  }

  /**
   * @description Save home certification agency
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IHomeCertificationAgencyApiSaveAttrs} payload
   *
   * @return {Promise<IHomeCertificationAgencyApiSaveResponse>} Promise<IHomeCertificationAgencyApiSaveResponse>
   */
  async homeCertificationAgency_saveHomeCertificationAgency(
    payload: IHomeCertificationAgencyApiSaveAttrs,
  ): Promise<IHomeCertificationAgencyApiSaveResponse> {
    try {
      const requestPayload: IBaseApiRequestPayload = {
        method: 'POST',
        url: payload.params?.id
          ? `${this.certificationAgencyItemsUrl}/update/${payload.params.id}`
          : `${this.certificationAgencyItemsUrl}/create`,
      }
      const response = await this[requestPayload.method](requestPayload.url, payload.body, { ...payload.requestConfig })

      return Promise.resolve(response)
    } catch (err) {
      return Promise.reject(err)
    }
  }

  /**
   * @description Get images
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IHomeCertificationAgencyApiGetImagesAttrs} payload
   *
   * @return {Promise<IHomeCertificationAgencyApiGetImagesResponse>} Promise<IHomeCertificationAgencyApiGetImagesResponse>
   */
  async homeCertificationAgency_getImages(
    payload: IHomeCertificationAgencyApiGetImagesAttrs,
  ): Promise<IHomeCertificationAgencyApiGetImagesResponse> {
    try {
      const response = await this.POST(
        `${this.certificationAgencyContentUrl}/list/${payload.params.itemsId}`,
        {},
        { ...payload.requestConfig },
      )

      return Promise.resolve(response)
    } catch (err) {
      return Promise.reject(err)
    }
  }

  /**
   * @description Update image
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IHomeCertificationAgencyApiUpdateImageAttrs} payload
   *
   * @return {Promise<IHomeCertificationAgencyApiUpdateImageResponse>} Promise<IHomeCertificationAgencyApiUpdateImageResponse>
   */
  async homeCertificationAgency_updateImage(
    payload: IHomeCertificationAgencyApiUpdateImageAttrs,
  ): Promise<IHomeCertificationAgencyApiUpdateImageResponse> {
    try {
      // Set Form Data
      let formData = new FormData()
      formData.append('image', payload.body.image)

      const response = await this.POST(`${this.certificationAgencyContentUrl}/update/${payload.params.contentId}`, formData, {
        ...payload.requestConfig,
      })

      return Promise.resolve(response)
    } catch (err) {
      return Promise.reject(err)
    }
  }

  /**
   * @description Delete image
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IHomeCertificationAgencyApiDeleteImageAttrs} payload
   *
   * @return {Promise<IHomeCertificationAgencyApiDeleteImageResponse>} Promise<IHomeCertificationAgencyApiDeleteImageResponse>
   */
  async homeCertificationAgency_deleteImage(
    payload: IHomeCertificationAgencyApiDeleteImageAttrs,
  ): Promise<IHomeCertificationAgencyApiDeleteImageResponse> {
    try {
      const response = await this.POST(
        `${this.certificationAgencyContentUrl}/delete/${payload.params.contentId}`,
        {},
        { ...payload.requestConfig },
      )

      return Promise.resolve(response)
    } catch (err) {
      return Promise.reject(err)
    }
  }
}
